import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { formatDate } from '@/lib/dateFormatter';
import { EMAIL_STATUS, EmailLog } from '@/services/emailLogs/emailLogs.type';
import type { ColumnDef } from '@tanstack/react-table';

export function getColumns(t: (key: string) => string): ColumnDef<EmailLog>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'emailRequestedId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.emailRequestId')} />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption text-primary">{row.getValue('emailRequestedId')}</div>
      ),
      meta: {
        label: 'Resource ID',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'message',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.message')} />,
      cell: ({ row }) => (
        <div className="w-[30rem] font-medium text-caption text-primary">
          <iframe
            title="email-iframe"
            ref={(iframe) => {
              if (!iframe) return;

              iframe.onload = () => {
                try {
                  const doc = iframe.contentDocument || iframe.contentWindow?.document;
                  if (!doc) return;

                  const height = doc.body.scrollHeight;
                  iframe.style.height = height + 'px';
                } catch {
                  // Cross-origin iframe content cannot always be measured.
                }
              };
            }}
            srcDoc={`
    <html>
      <head>
        <style>
          body { margin: 0; padding: 0; overflow: hidden; }
        </style>
      </head>
      <body>
        ${String(row.getValue('message'))}
      </body>
    </html>
  `}
            style={{ width: '100%', border: 'none' }}
          />
        </div>
      ),
      meta: {
        label: 'Message',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'emailSentBy',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.emailSentBy')} />
      ),
      cell: ({ row }) => {
        const email: string = row.getValue('emailSentBy');
        return (
          <a href={`mailto:${email}`} className="text-link font-medium underline">
            {email}
          </a>
        );
      },
      meta: {
        label: 'Sent By',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'emailSentTo',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.emailSentTo')} />
      ),
      cell: ({ row }) => {
        const email: string = row.getValue('emailSentTo');
        return (
          <a href={`mailto:${email}`} className="text-link font-medium underline">
            {email}
          </a>
        );
      },
      meta: {
        label: 'Sent To',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'emailSendStatus',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.emailSentStatus')} />
      ),
      cell: ({ row }) => {
        const status = row.getValue('emailSendStatus');
        return (
          <div className={`font-medium text-caption ${status === EMAIL_STATUS.SENT ? 'text-success' : 'text-warning'}`}>
            {status === EMAIL_STATUS.SENT ? t('common.page.table.sent') : t('common.page.table.pending')}
          </div>
        );
      },
      meta: {
        label: 'Status',
      },
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdOn',
      enableColumnFilter: true,
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdOn')} />,
      cell: ({ row }) => {
        const raw: number = row.getValue('createdDate');
        return (
          <div className="font-medium text-caption text-primary whitespace-nowrap">
            {formatDate(Number(raw), 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      meta: {
        label: 'Created On',
      },
      enableHiding: false,
    },
  ];
}
